import { FormEvent, useEffect, useMemo, useState } from "react";

import { useApiUrl } from "@refinedev/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  AlertCircle,
  BotIcon,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  DownloadIcon,
  Loader2,
  RefreshCw,
  SearchIcon,
  TimerIcon,
  WrenchIcon,
} from "lucide-react";

import { DatePickerWithRange } from "@/components/fields/date-picker";
import {
  ListView,
  ListViewHeader,
} from "@/components/refine-ui/views/list-view";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import { fetchMoodChatLogRun, fetchMoodChatLogRuns } from "./api";
import { useMoodChatLogsExport } from "./hooks/use-mood-chat-logs-export";
import type {
  JsonRecord,
  MoodChatLogEntry,
  MoodChatRunDetail,
  MoodChatRunFilters,
  MoodChatRunStatus,
  MoodChatRunSummary,
  MoodChatToolTrace,
} from "./types";

type DraftFilters = {
  from: string;
  to: string;
  query: string;
  conversationId: string;
  sessionId: string;
  uid: string;
  tool: string;
  status: string;
  limit: string;
};

type DisplayRow = [label: string, value: string];

type MoodChatPhaseEntry = {
  phase: string;
  durationMs?: number;
  timestamp?: string | null;
};

type ToolDiagnosticSummary = {
  insertId: string;
  label: string;
  durationMs?: number;
  found?: number;
  hitCount?: number;
  fields: DisplayRow[];
  payload: JsonRecord;
};

const TOOL_OPTIONS = [
  "searchEvents",
  "searchArtists",
  "searchVenues",
  "searchGenres",
];

const STATUS_OPTIONS: Array<{ label: string; value: MoodChatRunStatus }> = [
  { label: "Completed", value: "completed" },
  { label: "Failed", value: "failed" },
  { label: "Fallback", value: "fallback" },
  { label: "Rate limited", value: "rate_limited" },
];

export function MoodChatLogsPage() {
  const apiUrl = useApiUrl();
  const queryClient = useQueryClient();
  const [draftFilters, setDraftFilters] = useState<DraftFilters>(() => ({
    from: toDateTimeLocal(dayjs().subtract(24, "hour").startOf("day").toDate()),
    to: toDateTimeLocal(dayjs().endOf("day").toDate()),
    query: "",
    conversationId: "",
    sessionId: "",
    uid: "",
    tool: "all",
    status: "all",
    limit: "50",
  }));
  const [filters, setFilters] = useState<MoodChatRunFilters>(() =>
    toRunFilters(draftFilters),
  );
  const [pageTokens, setPageTokens] = useState<string[]>([""]);
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const { exportCsv, isExporting, exportedRunCount } = useMoodChatLogsExport(
    apiUrl,
    filters,
  );

  const pageToken = pageTokens[pageIndex] || undefined;
  const runsQuery = useQuery({
    queryKey: ["mood-chat-log-runs", apiUrl, filters, pageToken, refreshNonce],
    queryFn: () => fetchMoodChatLogRuns(apiUrl, { ...filters, pageToken }),
  });

  const runs = runsQuery.data?.data ?? [];
  const selectedRun = runs.find((run) => run.id === selectedRunId) ?? null;

  const detailQuery = useQuery({
    queryKey: [
      "mood-chat-log-run",
      apiUrl,
      selectedRunId,
      filters.from,
      filters.to,
    ],
    queryFn: () =>
      fetchMoodChatLogRun(apiUrl, selectedRunId!, {
        from: filters.from,
        to: filters.to,
      }),
    enabled: !!selectedRunId,
  });

  useEffect(() => {
    if (runsQuery.dataUpdatedAt > 0) {
      setLastRefreshedAt(new Date(runsQuery.dataUpdatedAt));
    }
  }, [runsQuery.dataUpdatedAt]);

  useEffect(() => {
    if (runs.length === 0) {
      setSelectedRunId(null);
      return;
    }

    if (!selectedRunId || !runs.some((run) => run.id === selectedRunId)) {
      setSelectedRunId(runs[0].id);
    }
  }, [runs, selectedRunId]);

  const stats = useMemo(() => getRunStats(runs), [runs]);

  function updateDraftFilter<K extends keyof DraftFilters>(
    key: K,
    value: DraftFilters[K],
  ) {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  }

  function updateDraftDateRange(range: { from?: Date; to?: Date } | undefined) {
    setDraftFilters((current) => ({
      ...current,
      from: range?.from
        ? toDateTimeLocal(dayjs(range.from).startOf("day").toDate())
        : "",
      to: range?.to
        ? toDateTimeLocal(dayjs(range.to).endOf("day").toDate())
        : "",
    }));
  }

  function applyFilters(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPageTokens([""]);
    setPageIndex(0);
    setFilters(toRunFilters(draftFilters));
  }

  function refresh() {
    setPageTokens([""]);
    setPageIndex(0);
    setSelectedRunId(null);
    queryClient.invalidateQueries({ queryKey: ["mood-chat-log-run"] });
    setRefreshNonce((current) => current + 1);
  }

  function goToNextPage() {
    const nextPageToken = runsQuery.data?.nextPageToken;
    if (!nextPageToken) return;

    setPageTokens((current) => {
      const next = [...current];
      next[pageIndex + 1] = nextPageToken;
      return next;
    });
    setPageIndex((current) => current + 1);
  }

  return (
    <ListView className="p-6 gap-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <ListViewHeader title="Mood Chat Logs" />
        <div className="flex flex-col items-start gap-1 xl:items-end">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={exportCsv}
              disabled={isExporting || runsQuery.isFetching}
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <DownloadIcon className="h-4 w-4" />
              )}
              {isExporting
                ? `Exporting… ${(exportedRunCount ?? 0).toLocaleString()}`
                : "Export CSV"}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={refresh}
              disabled={
                runsQuery.isFetching || detailQuery.isFetching || isExporting
              }
            >
              <RefreshCw
                className={cn("h-4 w-4", {
                  "animate-spin":
                    runsQuery.isFetching || detailQuery.isFetching,
                })}
              />
              Refresh
            </Button>
          </div>
          <span className="text-xs text-muted-foreground">
            {lastRefreshedAt
              ? `Last refreshed ${dayjs(lastRefreshedAt).format("HH:mm:ss")}`
              : "Not refreshed yet"}
          </span>
        </div>
      </div>

      <form
        onSubmit={applyFilters}
        className="grid grid-cols-1 gap-3 rounded-lg border bg-card p-4 md:grid-cols-2 xl:grid-cols-6"
      >
        <FilterField label="Date range" className="xl:col-span-2">
          <DatePickerWithRange
            key={`${draftFilters.from}-${draftFilters.to}`}
            className="[&_button]:w-full"
            defaultFrom={parseDraftDate(draftFilters.from)}
            defaultTo={parseDraftDate(draftFilters.to)}
            onDateRangeChanged={updateDraftDateRange}
          />
        </FilterField>
        <FilterField label="Status">
          <Select
            value={draftFilters.status}
            onValueChange={(value) => updateDraftFilter("status", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Tool">
          <Select
            value={draftFilters.tool}
            onValueChange={(value) => updateDraftFilter("tool", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="All tools" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tools</SelectItem>
              {TOOL_OPTIONS.map((tool) => (
                <SelectItem key={tool} value={tool}>
                  {tool}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Search" className="xl:col-span-2">
          <div className="flex gap-2">
            <Input
              value={draftFilters.query}
              onChange={(event) =>
                updateDraftFilter("query", event.target.value)
              }
              placeholder="Query or reply text"
            />
            <Button type="submit" className="gap-2">
              <SearchIcon className="h-4 w-4" />
              Apply
            </Button>
          </div>
        </FilterField>
        <FilterField label="Conversation ID" className="xl:col-span-2">
          <Input
            value={draftFilters.conversationId}
            onChange={(event) =>
              updateDraftFilter("conversationId", event.target.value)
            }
            placeholder="Optional"
          />
        </FilterField>
        <FilterField label="Session ID" className="xl:col-span-2">
          <Input
            value={draftFilters.sessionId}
            onChange={(event) =>
              updateDraftFilter("sessionId", event.target.value)
            }
            placeholder="Optional"
          />
        </FilterField>
        <FilterField label="User ID">
          <Input
            value={draftFilters.uid}
            onChange={(event) => updateDraftFilter("uid", event.target.value)}
            placeholder="Optional"
          />
        </FilterField>
        <FilterField label="Limit">
          <Input
            type="number"
            min={1}
            max={100}
            value={draftFilters.limit}
            onChange={(event) => updateDraftFilter("limit", event.target.value)}
          />
        </FilterField>
      </form>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <MetricCard
          label="Loaded Runs"
          value={runs.length.toString()}
          icon={<Clock3 className="h-4 w-4" />}
        />
        <MetricCard
          label="Completed"
          value={stats.completed.toString()}
          icon={<CheckCircle2 className="h-4 w-4" />}
        />
        <MetricCard
          label="Problems"
          value={(stats.failed + stats.rateLimited).toString()}
          icon={<AlertCircle className="h-4 w-4" />}
        />
        <MetricCard
          label="Tool Calls"
          value={stats.toolCallCount.toString()}
          icon={<WrenchIcon className="h-4 w-4" />}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(420px,0.6fr)]">
        <Card className="min-w-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base">Runs</CardTitle>
              <CardDescription>
                Structured Cloud Logging entries emitted by Brain.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1"
                disabled={pageIndex === 0 || runsQuery.isFetching}
                onClick={() =>
                  setPageIndex((current) => Math.max(0, current - 1))
                }
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1"
                disabled={
                  !runsQuery.data?.nextPageToken || runsQuery.isFetching
                }
                onClick={goToNextPage}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {runsQuery.isLoading ? (
              <RunTableSkeleton />
            ) : runsQuery.isError ? (
              <EmptyState
                title="Could not load logs"
                description="The admin API could not query Cloud Logging. Check service account permissions before blaming the UI."
              />
            ) : runs.length === 0 ? (
              <EmptyState
                title="No chat logs"
                description="Try widening the date range or wait for the newly structured logs to arrive."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[150px]">Time</TableHead>
                    <TableHead>Query</TableHead>
                    <TableHead className="w-[150px]">Tools</TableHead>
                    <TableHead className="w-[120px]">Status</TableHead>
                    <TableHead className="w-[100px] text-right">Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map((run) => (
                    <TableRow
                      key={run.id}
                      className={cn("cursor-pointer", {
                        "bg-muted/70": run.id === selectedRunId,
                      })}
                      onClick={() => setSelectedRunId(run.id)}
                    >
                      <TableCell>
                        <div className="text-sm font-medium">
                          {formatTimestamp(run.timestamp)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatLocation(run)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[620px]">
                          <p className="line-clamp-2 text-sm font-medium">
                            {run.message || "No user message logged"}
                          </p>
                          <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">
                            {run.reply || getErrorMessage(run.error) || run.id}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <ToolBadges tools={run.toolCalls} />
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={run.status} />
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs text-muted-foreground">
                        {formatElapsed(run.elapsedMs)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <RunInspector
          summary={selectedRun}
          detail={detailQuery.data ?? null}
          isLoading={detailQuery.isLoading || detailQuery.isFetching}
          isError={detailQuery.isError}
        />
      </div>
    </ListView>
  );
}

function FilterField({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function MetricCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {label}
        </CardTitle>
        <div className="rounded-md bg-primary/10 p-2 text-primary">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

function RunInspector({
  summary,
  detail,
  isLoading,
  isError,
}: {
  summary: MoodChatRunSummary | null;
  detail: MoodChatRunDetail | null;
  isLoading: boolean;
  isError: boolean;
}) {
  const terminalPayload = getTerminalPayload(detail);
  const toolTraces = getToolTraces(terminalPayload);
  const entries = detail?.entries ?? [];
  const toolDiagnostics = getToolDiagnostics(entries);
  const phaseEntries = getPhaseEntries(entries);

  if (!summary) {
    return (
      <Card>
        <CardContent className="flex min-h-[420px] items-center justify-center">
          <EmptyState
            title="Select a run"
            description="Pick a chat run from the table to inspect the query, model reply, tool calls, and raw log payload."
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="min-w-0 xl:sticky xl:top-4">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-base">
              <BotIcon className="h-4 w-4" />
              Run detail
            </CardTitle>
            <CardDescription className="mt-1 truncate font-mono">
              {summary.id}
            </CardDescription>
          </div>
          <StatusBadge status={summary.status} />
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="summary" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="summary">Overview</TabsTrigger>
            <TabsTrigger value="tools">Tools</TabsTrigger>
            <TabsTrigger value="logs">Timeline</TabsTrigger>
            <TabsTrigger value="raw">Raw</TabsTrigger>
          </TabsList>
          <TabsContent value="summary" className="mt-4">
            <InspectorScroll>
              <KeyValueGrid summary={summary} />
              <TextBlock label="User query" value={summary.message} />
              <TextBlock label="AI reply" value={summary.reply} />
              <PhaseTimingList
                entries={phaseEntries}
                totalElapsedMs={summary.elapsedMs}
              />
              {isLoading && !detail ? <Skeleton className="h-10" /> : null}
              {isError ? (
                <EmptyState
                  title="Detail logs failed"
                  description="The summary loaded, but the detail Cloud Logging query failed. Check the backend log filter or service account permissions."
                />
              ) : null}
              {summary.error ? (
                <JsonBlock title="Error" value={summary.error} />
              ) : null}
            </InspectorScroll>
          </TabsContent>
          <TabsContent value="tools" className="mt-4">
            <InspectorScroll>
              {isLoading && !detail ? (
                <RunTableSkeleton />
              ) : toolTraces.length === 0 ? (
                <EmptyState
                  title="No tools recorded"
                  description="This run either did not call tools or predates the structured tool trace logs."
                />
              ) : (
                <>
                  <ToolRunSummary
                    traces={toolTraces}
                    diagnostics={toolDiagnostics}
                  />
                  {toolTraces.map((trace, index) => (
                    <ToolTraceCard
                      key={`${trace.name}-${trace.ref ?? index}`}
                      trace={trace}
                      diagnostics={toolDiagnostics[trace.name] ?? []}
                    />
                  ))}
                </>
              )}
            </InspectorScroll>
          </TabsContent>
          <TabsContent value="logs" className="mt-4">
            <InspectorScroll>
              {isLoading && !detail ? (
                <RunTableSkeleton />
              ) : (
                <LogTimeline entries={entries} />
              )}
            </InspectorScroll>
          </TabsContent>
          <TabsContent value="raw" className="mt-4">
            <InspectorScroll>
              <JsonBlock
                title="Terminal payload"
                value={terminalPayload ?? summary}
              />
            </InspectorScroll>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function KeyValueGrid({ summary }: { summary: MoodChatRunSummary }) {
  const values = [
    ["Timestamp", formatTimestamp(summary.timestamp)],
    ["Elapsed", formatElapsed(summary.elapsedMs)],
    ["Location", formatLocation(summary)],
    [
      "Conversation",
      summary.conversationId ?? summary.requestedConversationId ?? "—",
    ],
    ["Session", summary.sessionId ?? "—"],
    ["User", summary.uid ?? "anonymous"],
  ];

  return (
    <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
      {values.map(([label, value]) => (
        <div key={label} className="rounded-md border bg-muted/20 p-2">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="mt-1 break-all font-medium">{value}</div>
        </div>
      ))}
    </div>
  );
}

function TextBlock({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium uppercase text-muted-foreground">
        {label}
      </div>
      <div className="rounded-md border bg-muted/20 p-3 text-sm leading-relaxed whitespace-pre-wrap">
        {value || "—"}
      </div>
    </div>
  );
}

function InspectorScroll({ children }: { children: React.ReactNode }) {
  return (
    <ScrollArea className="max-h-[720px] pr-3 xl:h-[calc(100vh-260px)] xl:max-h-none">
      <div className="space-y-4">{children}</div>
    </ScrollArea>
  );
}

function PhaseTimingList({
  entries,
  totalElapsedMs,
}: {
  entries: MoodChatPhaseEntry[];
  totalElapsedMs?: number | null;
}) {
  if (!entries.length && totalElapsedMs === undefined) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-medium uppercase text-muted-foreground">
          Timing
        </div>
        <Badge variant="secondary">Total {formatElapsed(totalElapsedMs)}</Badge>
      </div>
      {entries.length ? (
        <div className="space-y-1.5">
          {entries.map((entry, index) => (
            <div
              key={`${entry.phase}-${index}`}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-md border bg-muted/20 px-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <div className="truncate font-medium capitalize">
                  {entry.phase.replace(/_/g, " ")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatTimestamp(entry.timestamp)}
                </div>
              </div>
              <div className="font-mono text-xs text-muted-foreground">
                {formatElapsed(entry.durationMs)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
          Phase timing logs were not found for this run.
        </div>
      )}
    </div>
  );
}

function ToolTraceCard({
  trace,
  diagnostics,
}: {
  trace: MoodChatToolTrace;
  diagnostics: MoodChatLogEntry[];
}) {
  const output = trace.output as
    | { count?: number; items?: Array<{ id?: string; name?: string }> }
    | undefined;
  const diagnosticSummaries = diagnostics.map(toDiagnosticSummary);

  return (
    <div className="rounded-lg border bg-muted/20 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono">
            {trace.name}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {trace.outputCount ?? output?.count ?? 0} results
          </span>
        </div>
        <Badge variant={trace.status === "completed" ? "secondary" : "outline"}>
          {trace.status}
        </Badge>
      </div>
      <div className="mt-3 space-y-3">
        <KeyValueRows title="Model input" value={trace.input} />
        {diagnosticSummaries.length ? (
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase text-muted-foreground">
              Typesense diagnostics
            </div>
            {diagnosticSummaries.map((diagnostic) => (
              <DiagnosticCard
                key={diagnostic.insertId}
                diagnostic={diagnostic}
              />
            ))}
          </div>
        ) : null}
        {output?.items?.length ? (
          <div>
            <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">
              Output preview
            </div>
            <div className="space-y-1">
              {output.items.slice(0, 8).map((item, index) => (
                <div
                  key={`${item.id ?? item.name ?? "item"}-${index}`}
                  className="flex items-center justify-between gap-2 rounded-md border bg-background px-2 py-1.5 text-xs"
                >
                  <span className="truncate font-medium">
                    {item.name ?? "Unnamed"}
                  </span>
                  <span className="truncate font-mono text-muted-foreground">
                    {item.id ?? "—"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <KeyValueRows title="Tool output" value={trace.output} />
        )}
        <Disclosure title="Raw tool trace">
          <JsonBlock title="Trace" value={trace} compact />
        </Disclosure>
      </div>
    </div>
  );
}

function LogTimeline({ entries }: { entries: MoodChatLogEntry[] }) {
  if (entries.length === 0) {
    return (
      <EmptyState
        title="No detail entries"
        description="The list entry loaded, but the detail query returned no sibling log events."
      />
    );
  }

  return (
    <div className="space-y-2">
      {entries.map((entry) => (
        <TimelineEntryCard key={entry.insertId} entry={entry} />
      ))}
    </div>
  );
}

function ToolRunSummary({
  traces,
  diagnostics,
}: {
  traces: MoodChatToolTrace[];
  diagnostics: Record<string, MoodChatLogEntry[]>;
}) {
  const diagnosticCount = Object.values(diagnostics).reduce(
    (count, entries) => count + entries.length,
    0,
  );
  const totalTypesenseMs = Object.values(diagnostics)
    .flat()
    .reduce(
      (total, entry) => total + (getNumber(entry.payload.durationMs) ?? 0),
      0,
    );
  const completed = traces.filter(
    (trace) => trace.status === "completed",
  ).length;

  return (
    <div className="grid grid-cols-3 gap-2">
      <MiniMetric label="Completed" value={`${completed}/${traces.length}`} />
      <MiniMetric label="Diagnostics" value={diagnosticCount.toString()} />
      <MiniMetric label="Typesense" value={formatElapsed(totalTypesenseMs)} />
    </div>
  );
}

function MiniMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-background p-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 truncate font-mono text-sm font-medium">{value}</div>
    </div>
  );
}

function DiagnosticCard({ diagnostic }: { diagnostic: ToolDiagnosticSummary }) {
  return (
    <div className="space-y-2 rounded-md border bg-background p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Badge variant="outline" className="font-mono">
          {diagnostic.label}
        </Badge>
        <div className="flex flex-wrap items-center gap-1.5">
          {diagnostic.durationMs !== undefined ? (
            <Badge variant="secondary">
              {formatElapsed(diagnostic.durationMs)}
            </Badge>
          ) : null}
          {diagnostic.found !== undefined ? (
            <Badge variant="secondary">{diagnostic.found} found</Badge>
          ) : null}
          {diagnostic.hitCount !== undefined ? (
            <Badge variant="secondary">{diagnostic.hitCount} hits</Badge>
          ) : null}
        </div>
      </div>
      <KeyValueRows value={diagnostic.fields} />
      <Disclosure title="Raw diagnostic payload">
        <JsonBlock title="Payload" value={diagnostic.payload} compact />
      </Disclosure>
    </div>
  );
}

function TimelineEntryCard({ entry }: { entry: MoodChatLogEntry }) {
  const fields = toTimelineFields(entry);

  return (
    <div className="rounded-lg border bg-muted/20 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Badge variant="outline" className="font-mono">
          {entry.event}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {formatTimestamp(entry.timestamp)}
        </span>
      </div>
      {fields.length ? (
        <div className="mt-2 grid grid-cols-1 gap-1.5 text-xs sm:grid-cols-2">
          {fields.map(([label, value]) => (
            <div key={label} className="rounded-md bg-background px-2 py-1.5">
              <span className="text-muted-foreground">{label}: </span>
              <span className="break-words font-medium">{value}</span>
            </div>
          ))}
        </div>
      ) : null}
      <Disclosure title="Payload">
        <JsonBlock title="Payload" value={entry.payload} compact />
      </Disclosure>
    </div>
  );
}

function KeyValueRows({ title, value }: { title?: string; value: unknown }) {
  const rows = toDisplayRows(value);

  if (!rows.length) return null;

  return (
    <div className="space-y-1.5">
      {title ? (
        <div className="text-xs font-medium uppercase text-muted-foreground">
          {title}
        </div>
      ) : null}
      <div className="space-y-1.5">
        {rows.map(([label, rowValue]) => (
          <div
            key={label}
            className="grid grid-cols-[112px_minmax(0,1fr)] gap-2 rounded-md border bg-background px-2 py-1.5 text-xs"
          >
            <div className="truncate text-muted-foreground">{label}</div>
            <div className="break-words font-medium">{rowValue}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Disclosure({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <details className="group rounded-md border bg-background p-2">
      <summary className="cursor-pointer text-xs font-medium uppercase text-muted-foreground">
        {title}
      </summary>
      <div className="mt-2">{children}</div>
    </details>
  );
}

function JsonBlock({
  title,
  value,
  compact = false,
}: {
  title: string;
  value: unknown;
  compact?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium uppercase text-muted-foreground">
        {title}
      </div>
      <pre
        className={cn(
          "overflow-auto rounded-md border bg-background p-3 font-mono text-xs leading-relaxed",
          compact ? "max-h-48" : "max-h-[620px]",
        )}
      >
        {JSON.stringify(value ?? null, null, 2)}
      </pre>
    </div>
  );
}

function StatusBadge({ status }: { status: MoodChatRunStatus | string }) {
  const className =
    status === "completed"
      ? "border-green-500/20 bg-green-500/10 text-green-700 dark:text-green-300"
      : status === "failed" || status === "fallback"
        ? "border-destructive/20 bg-destructive/10 text-destructive"
        : status === "rate_limited"
          ? "border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300"
          : "";

  return (
    <Badge variant="outline" className={cn("capitalize", className)}>
      {status.replace("_", " ")}
    </Badge>
  );
}

function ToolBadges({ tools }: { tools: string[] }) {
  if (!tools.length) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {tools.slice(0, 3).map((tool) => (
        <Badge key={tool} variant="outline" className="font-mono text-[10px]">
          {tool.replace("search", "") || tool}
        </Badge>
      ))}
      {tools.length > 3 ? (
        <Badge variant="secondary" className="text-[10px]">
          +{tools.length - 3}
        </Badge>
      ) : null}
    </div>
  );
}

function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
      <TimerIcon className="mb-3 h-8 w-8 text-muted-foreground" />
      <div className="text-sm font-medium">{title}</div>
      <div className="mt-1 max-w-md text-xs text-muted-foreground">
        {description}
      </div>
    </div>
  );
}

function RunTableSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 8 }).map((_, index) => (
        <Skeleton key={index} className="h-14 w-full" />
      ))}
    </div>
  );
}

function getRunStats(runs: MoodChatRunSummary[]) {
  return runs.reduce(
    (stats, run) => ({
      completed: stats.completed + (run.status === "completed" ? 1 : 0),
      failed:
        stats.failed +
        (run.status === "failed" || run.status === "fallback" ? 1 : 0),
      rateLimited: stats.rateLimited + (run.status === "rate_limited" ? 1 : 0),
      toolCallCount: stats.toolCallCount + run.toolCalls.length,
    }),
    { completed: 0, failed: 0, rateLimited: 0, toolCallCount: 0 },
  );
}

function toRunFilters(filters: DraftFilters): MoodChatRunFilters {
  return {
    from: toIsoDate(filters.from),
    to: toIsoDate(filters.to),
    query: filters.query.trim() || undefined,
    conversationId: filters.conversationId.trim() || undefined,
    sessionId: filters.sessionId.trim() || undefined,
    uid: filters.uid.trim() || undefined,
    tool: filters.tool === "all" ? undefined : filters.tool,
    status: filters.status === "all" ? undefined : filters.status,
    limit: Number(filters.limit) || 50,
  };
}

function toDateTimeLocal(date: Date) {
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function parseDraftDate(value: string) {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function toIsoDate(value: string) {
  if (!value) return undefined;
  return new Date(value).toISOString();
}

function formatTimestamp(value?: string | null) {
  if (!value) return "—";
  return dayjs(value).format("MMM D, HH:mm:ss");
}

function formatElapsed(value?: number | null) {
  if (value === null || value === undefined) return "—";
  if (value < 1000) return `${Math.round(value)} ms`;
  return `${(value / 1000).toFixed(1)} s`;
}

function formatLocation(run: MoodChatRunSummary) {
  const city = run.location?.city;
  const country = run.location?.country;
  if (!city && !country) return "Unknown location";
  return [city, country].filter(Boolean).join(", ");
}

function getErrorMessage(error: unknown) {
  if (!error || typeof error !== "object") return null;
  const record = error as { message?: unknown };
  return typeof record.message === "string" ? record.message : null;
}

function getTerminalPayload(
  detail: MoodChatRunDetail | null,
): JsonRecord | null {
  if (!detail?.entries.length) return null;
  const terminalEntry = [...detail.entries]
    .reverse()
    .find((entry) =>
      [
        "mood_chat.run.completed",
        "mood_chat.run.failed",
        "mood_chat.run.rate_limited",
      ].includes(entry.event),
    );

  return (
    terminalEntry?.payload ?? detail.entries[detail.entries.length - 1].payload
  );
}

function getToolTraces(payload: JsonRecord | null): MoodChatToolTrace[] {
  const traces = payload?.toolTraces;
  return Array.isArray(traces) ? (traces as MoodChatToolTrace[]) : [];
}

function getToolDiagnostics(entries: MoodChatLogEntry[]) {
  return entries.reduce<Record<string, MoodChatLogEntry[]>>(
    (grouped, entry) => {
      if (!entry.event.startsWith("mood_chat.tool.")) return grouped;

      const tool = entry.payload.tool;
      if (typeof tool !== "string") return grouped;

      grouped[tool] = [...(grouped[tool] ?? []), entry];
      return grouped;
    },
    {},
  );
}

function getPhaseEntries(entries: MoodChatLogEntry[]): MoodChatPhaseEntry[] {
  return entries
    .filter((entry) => entry.event === "mood_chat.phase.completed")
    .map((entry) => ({
      phase: getString(entry.payload.phase) ?? "unknown",
      durationMs: getNumber(entry.payload.durationMs),
      timestamp: entry.timestamp,
    }));
}

function toDiagnosticSummary(entry: MoodChatLogEntry): ToolDiagnosticSummary {
  const payload = entry.payload;
  const searchConfig =
    asRecord(payload.searchConfig) ?? asRecord(payload.relaxedSearchConfig);
  const fields: DisplayRow[] = [];

  if (searchConfig) {
    addRow(fields, "Query", searchConfig.q);
    addRow(fields, "Filters", searchConfig.filter_by);
    addRow(fields, "Query by", searchConfig.query_by);
    addRow(fields, "Sort", searchConfig.sort_by);
    addRow(fields, "Per page", searchConfig.per_page);
  }

  addRow(fields, "City", payload.city);
  addRow(fields, "Area", payload.area);
  addRow(fields, "Area resolution", payload.areaResolution);
  addRow(fields, "Error", payload.error ?? payload.message);

  return {
    insertId: entry.insertId,
    label: entry.event.replace("mood_chat.tool.", "").replace(/_/g, " "),
    durationMs: getNumber(payload.durationMs),
    found: getNumber(payload.found),
    hitCount: getNumber(payload.hitCount),
    fields,
    payload,
  };
}

function toTimelineFields(entry: MoodChatLogEntry): DisplayRow[] {
  const payload = entry.payload;
  const fields: DisplayRow[] = [];
  const durationMs = getNumber(payload.durationMs);
  const elapsedMs = getNumber(payload.elapsedMs);

  addRow(fields, "Phase", payload.phase);
  if (durationMs !== undefined)
    addRow(fields, "Duration", formatElapsed(durationMs));
  if (elapsedMs !== undefined)
    addRow(fields, "Elapsed", formatElapsed(elapsedMs));
  addRow(fields, "Tool", payload.tool);
  addRow(fields, "Query", payload.userQuery);
  addRow(fields, "Status", payload.status);
  addRow(fields, "Found", payload.found);
  addRow(fields, "Hits", payload.hitCount);
  addRow(fields, "Error", payload.error ?? payload.message);

  return fields;
}

function toDisplayRows(value: unknown): DisplayRow[] {
  if (Array.isArray(value)) {
    return value.every(
      (row): row is DisplayRow =>
        Array.isArray(row) && row.length === 2 && typeof row[0] === "string",
    )
      ? value
      : [];
  }

  const record = asRecord(value);
  if (!record) return [];

  return Object.entries(record).reduce<DisplayRow[]>(
    (rows, [key, rowValue]) => {
      addRow(rows, key, rowValue);
      return rows;
    },
    [],
  );
}

function addRow(rows: DisplayRow[], label: string, value: unknown) {
  if (value === undefined || value === null || value === "") return;
  rows.push([formatLabel(label), formatDisplayValue(value)]);
}

function formatLabel(value: string) {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatDisplayValue(value: unknown): string {
  if (typeof value === "string") return truncate(value);
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value))
    return truncate(value.map(formatDisplayValue).join(", "));
  if (typeof value === "object" && value)
    return truncate(JSON.stringify(value));
  return String(value);
}

function truncate(value: string, maxLength = 260) {
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
}

function asRecord(value: unknown): JsonRecord | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as JsonRecord)
    : null;
}

function getString(value: unknown) {
  return typeof value === "string" ? value : undefined;
}

function getNumber(value: unknown) {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

export default MoodChatLogsPage;
